import { AppError, ERROR_CODE } from '../util/AppError';
import { AXIOS_NETWORK_ERROR } from './constants';

type BackendValidationError = {
  entity?: string;
  property?: string;
  message?: string;
};

type BackendErrorBody = {
  code?: string;
  message?: string;
  messageKey?: string;
  defaultMessage?: string | null;
  errors?: BackendValidationError[];
};

const ERROR_CODE_VALUES = new Set<string>(Object.values(ERROR_CODE));

function isKnownErrorCode(code?: string): code is ERROR_CODE {
  return Boolean(code && ERROR_CODE_VALUES.has(code));
}

function mapBackendCode(rawCode?: string, body?: BackendErrorBody): ERROR_CODE | undefined {
  if (!rawCode) return undefined;

  if (rawCode.startsWith('ERR_')) {
    if (isKnownErrorCode(rawCode)) return rawCode;
    switch (rawCode) {
      case 'ERR_CLIENT_NOT_FOUND':
        return ERROR_CODE.ERR_BAD_PATIENTID;
      case 'ERR_DEVICE_STATUS_NOT_FOUND':
        return ERROR_CODE.ERR_DEVICE_NOT_FOUND;
      case 'ERR_DEVICE_NOT_AVAILABLE':
        return ERROR_CODE.ERR_CLIENT_DEVICE_ALREADY_ASSIGNED;
      default:
        return undefined;
    }
  }

  switch (rawCode) {
    case 'error.user.unauthorized':
      return ERROR_CODE.ERR_UNAUTHORIZED;
    case 'error.device.already_assigned':
      return ERROR_CODE.ERR_DEVICE_ALREADY_ASSIGNED;
    case 'error.client.device_already_assigned':
    case 'error.device.assignment_changed_in_meantime':
      return ERROR_CODE.ERR_CLIENT_DEVICE_ALREADY_ASSIGNED;
    case 'error.resource.not_found':
      return ERROR_CODE.ERR_BAD_PATIENTID;
    case 'error.healthdot.does.not.exist':
      // Scanned Healthdot is not provisioned in the backend, so it can't be paired.
      return ERROR_CODE.ERR_DEVICE_NOT_REGISTERED;
    case 'error.device.healthdot_not_available':
    case 'error.healthdot.not_available':
      // A spent Healthdot keeps its identity as USED, and a USED identity is never taken into
      // use again (HQS-2221). The portal refuses to assign it (the first key, on the
      // device-assignment PUT); device-adapter refuses to add a device for it when its record
      // is missing (the second, as a field error on the serial).
      return ERROR_CODE.ERR_HEALTHDOT_USED;
    case 'error.device.exists':
      return ERROR_CODE.ERR_DEVICE_ALREADY_ASSIGNED;
    case 'error.phone_number.phone_number_already_used':
      return ERROR_CODE.ERR_PHONE_NUMBER_ALREADY_USED;
    case 'error.bad.argument': {
      // Device-adapter can return a generic key with structured field errors.
      const hasSerialError = (body?.errors || []).some(
        (item) =>
          String(item.property || '').toLowerCase() === 'serialnumber' ||
          String(item.property || '').toLowerCase() === 'barcode',
      );
      return hasSerialError ? ERROR_CODE.ERR_BAD_BARCODE : ERROR_CODE.ERR_SERVER;
    }
    default:
      return undefined;
  }
}

export function toAppError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
  fallbackCode: ERROR_CODE = ERROR_CODE.ERR_UNKNOWN,
): AppError {
  if (err instanceof AppError) return err;

  const body = (err?.response?.data || {}) as BackendErrorBody;
  const message =
    body.message ||
    body.defaultMessage ||
    body.errors?.[0]?.message ||
    err?.message ||
    'Unknown error';

  if (message === AXIOS_NETWORK_ERROR) {
    return new AppError(ERROR_CODE.ERR_NETWORK, message);
  }

  const rawCode = body.code || body.messageKey || body.errors?.[0]?.message;
  const mappedCode = mapBackendCode(rawCode, body) || fallbackCode;
  return new AppError(mappedCode, message);
}
